// Property-data helpers. Pure functions only — no side effects, no React.

// Pull the fields we display, normalizing across Attom's nested shape.
// Attom returns 0 for missing numeric fields (not null), so we treat 0 as absent.
// Multiple field paths are tried in priority order — Attom field names vary by
// property type and API plan.
export const propFacts = (data) => {
  const p = data?.property?.[0];
  // Mocked property data (ATTOM key currently rejected — see api/property.js)
  // is fabricated per-address, not real. Treat it as absent everywhere: don't
  // show it as a fact card, and don't hand it to AI prompts as ground truth
  // alongside real web-searched comps — that produces confidently wrong output.
  if (!p || p._mocked) return null;
  const b = p.building;
  const r = b?.rooms;
  const t = p.assessment?.assessed;

  // Use first truthy (> 0) value across known Attom field aliases.
  const pick = (...vals) => vals.find((v) => v != null && v > 0) ?? null;

  const beds  = pick(r?.beds, r?.bedrooms);
  const baths = pick(
    r?.bathstotal,
    r?.bathscalculated,
    r?.bathsfull != null ? (r.bathsfull || 0) + (r.bathshalf || 0) * 0.5 : null,
  );
  const sqft      = pick(b?.size?.bldgsize, b?.size?.universalsize, b?.size?.grosssize);
  const year_built = pick(b?.yearbuilt, b?.summary?.yearbuilt, p.summary?.yearbuilt);
  const lot_sqft  = pick(p.lot?.lotsize2, p.lot?.lotsize1);
  const assessed  = pick(t?.assdttlvalue);

  return { beds, baths, sqft, year_built, lot_sqft, assessed };
};

// Render a compact key-value list of facts for prompts.
export const propFactsLine = (data) => {
  const f = propFacts(data);
  if (!f) return "";
  const parts = [
    f.beds != null && `${f.beds} beds`,
    f.baths != null && `${f.baths} baths`,
    f.sqft != null && `${f.sqft} sqft`,
    f.year_built != null && `built ${f.year_built}`,
    f.lot_sqft != null && `lot ${f.lot_sqft} sqft`,
    f.assessed != null && `assessed $${Number(f.assessed).toLocaleString()}`,
  ].filter(Boolean);
  return parts.join(" / ");
};

// Extract a "$X – $Y" price range from free-form AI output.
// Strategy: prefer the labeled "Fair listing-price range:" line to avoid
// matching $/sqft values in comp bullets. Falls back to the first
// bare range pattern. Rejects anything below $50k as a parsing artifact.
export const parsePriceRange = (text) => {
  if (!text) return null;

  const RANGE_RE = /\$\s*([\d.,]+)\s*([MmKk])?\s*[–\-—to]+\s*\$\s*([\d.,]+)\s*([MmKk])?/;
  // Try anchored match on the labeled line first.
  const labeled = text.match(
    /fair[- ](?:listing[- ])?price[- ]range[^$]*?(\$\s*[\d.,]+\s*[MmKk]?\s*[–\-—to]+\s*\$\s*[\d.,]+\s*[MmKk]?)/i
  );
  const m = (labeled ? labeled[1] : text).match(RANGE_RE);
  if (!m) return null;

  const parse = (s, suf) => {
    let n = parseFloat(s.replace(/,/g, ""));
    if (isNaN(n)) return null;
    if (/m/i.test(suf || "")) n *= 1_000_000;
    if (/k/i.test(suf || "")) n *= 1_000;
    return n;
  };
  const low  = parse(m[1], m[2]);
  const high = parse(m[3], m[4]);
  // Sanity floor — no real property is worth under $50k.
  if (low == null || high == null || low < 50_000 || high < 50_000) return null;
  return { low, high, midpoint: Math.round((low + high) / 2) };
};

export const fmtMoney = (n) =>
  n == null ? "—" : `$${Math.round(n).toLocaleString()}`;
